import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"

export type OrgListItem = {
  public_id: string
  name: string
  created_at?: string
  teams_count?: number
}

export type OrgListResponse = {
  ok: true
  items: OrgListItem[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
  }
}

export function useOrganizations(
  params: { page?: number; pageSize?: number; search?: string } = {}
) {
  const { page = 1, pageSize = 20, search = "" } = params
  return useQuery<OrgListResponse>({
    queryKey: ["admin-organizations", page, pageSize, search],
    queryFn: async () => {
      const res = await (client.admin.organizations as any)["$get"]({
        query: {
          page,
          pageSize,
          search: search || undefined,
        },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "ORG_LIST_FAILED",
          new Error(err.message || "Failed to list organizations")
        )
      }
      return res.json() as Promise<OrgListResponse>
    },
  })
}

export type CreateOrgPayload = { name: string }
export type CreateOrgResponse = {
  ok: true
  organization: {
    public_id: string
    name: string
    created_at?: string
  }
}

export function useCreateOrganization() {
  const qc = useQueryClient()
  return useMutation<CreateOrgResponse, APIError, CreateOrgPayload>({
    mutationFn: async ({ name }) => {
      const res = await (client.admin.organizations as any)["$post"]({ json: { name } })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "ORG_CREATE_FAILED",
          new Error(err.message || "Failed to create organization")
        )
      }
      return res.json() as Promise<CreateOrgResponse>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-organizations"] })
    },
  })
}

export type AttachTeamPayload = { orgPublicId: string; teamPublicId: string }
export type AttachTeamResponse = {
  ok: true
  organization: { public_id: string; name: string }
  team: { public_id: string }
  attached: boolean
}

export function useAttachTeamToOrganization() {
  const qc = useQueryClient()
  return useMutation<AttachTeamResponse, APIError, AttachTeamPayload>({
    mutationFn: async ({ orgPublicId, teamPublicId }) => {
      const res = await (client.admin.organizations[":orgPublicId"] as any)["attach-team"]["$post"](
        {
          param: { orgPublicId },
          json: { teamPublicId },
        }
      )
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "ORG_ATTACH_TEAM_FAILED",
          new Error(err.message || "Failed to attach team")
        )
      }
      return res.json() as Promise<AttachTeamResponse>
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin-organizations"] })
      qc.invalidateQueries({ queryKey: ["admin-organization", variables.orgPublicId] })
    },
  })
}

// useCreateTeamForOrganization was removed — use useCreateTeam from useTeams
// and pass `organizationPublicId` in the payload.
